import React from "react";
import submit from "submit-form";
import { getStripeDate } from "../../../utils/format-stripe-date";
import ToolTip from "../../../parts/tooltip";
import getSymbolFromCurrency from "currency-symbol-map";
import RangeSelectors from "../../../parts/filters/range-selectors";
import { formatNumber } from "../../../utils/format-sub";
import PaymentsTable from "./components/PaymentsTable";
import PayoutsTable from "./components/PayoutsTable";
import CustomLoader from "../../../parts/custom-loader";
import SvgIcon from "../../../parts/SvgIcon";

const EarningSelector = ({
  active = false,
  title = "",
  total = 0,
  from = false,
  tooltipMessage = "",
  currency,
  date,
}) => {
  return (
    <div className={`earning-selector ${active ? "active" : ""}`}>
      <header>
        <span>{title}</span>
        <ToolTip id={`tooltip-${title}`} body={tooltipMessage}>
          <SvgIcon
            src="/img/new-icons/question-circle.svg"
            noFilterOnLight={true}
          />
        </ToolTip>
      </header>
      <div className="earning-quantity">
        <h1>{formatNumber(total, currency)}</h1>
        {from ? (
          <p className="p1">from {formatNumber(from, currency)}</p>
        ) : null}
      </div>
      <p className="p1">{date}</p>
    </div>
  );
};

const getGraphRange = (state) => {
  switch (state) {
    case "all":
      return 0;
    case "last-3":
      return -3;
    case "last-5":
      return -5;
    case "last-year":
      return -12;
    default:
      return 0;
  }
};

class View extends React.PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      tooltip: {
        show: false,
        y: 0,
        x: 0,
        content: "",
      },
      paymentsSorting: "desc",
      payoutsSorting: "desc",
      loading: false,
    };
    this.chartObject = null;
    this.date = getStripeDate(new Date() / 1000);
    this.handlePaymentsSorting = this.handlePaymentsSorting.bind(this);
    this.handlePayoutsSorting = this.handlePayoutsSorting.bind(this);
    this.handleDateSelector = this.handleDateSelector.bind(this);
    this.handleDataLoad = this.handleDataLoad.bind(this);
  }

  componentDidMount() {
    if (this.props.loadData) {
      this.initFn();
    }
  }

  componentDidUpdate(prevProps) {
    const { selector, loadData } = this.props;

    if (
      loadData &&
      (prevProps.loadData !== loadData || prevProps.selector !== selector)
    ) {
      this.initFn();
    }
  }

  initFn() {
    const { feIncomes = {} } = this.context.locals;
    const { allNetGrowing, currency } = feIncomes;

    const setTooltip = (props) => {
      const { content = "", y = 0, x = 0, show = false } = props;
      this.setState({
        tooltip: {
          show,
          content,
          x,
          y,
        },
      });
    };

    const Chartist = require("chartist");

    this.chartObject = new Chartist.Bar(
      ".graph",
      {
        labels: allNetGrowing
          .slice(getGraphRange("all"))
          .map(({ label }) => label),
        series: allNetGrowing.slice(getGraphRange("all")).map(({ net }) => net),
      },
      {
        distributeSeries: true,
        showGrid: false,

        axisY: {
          labelInterpolationFnc: function (value) {
            return value > 999 ? value / 1000 + "k" : value;
          },
          labelOffset: {
            x: -15,
          },
          showLine: false,
        },
        height: 250,
      }
    ).on("draw", function (data) {
      if (data.type === "bar") {
        data.element.attr({
          style: "stroke-width: 50px; stroke: var(--information);",
        });

        data.element._node.addEventListener("mouseenter", ({ target }) => {
          setTooltip({
            show: true,
            x: target.getAttribute("x1") - 45,
            y: target.getAttribute("y2") - 25,
            content: formatNumber(target.getAttribute("ct:value"), currency),
          });
        });

        data.element._node.addEventListener("mouseleave", () => {
          setTooltip({
            show: false,
          });
        });
      }
      if (data.type === "grid" && data.axis.counterUnits.dir === "horizontal") {
        data.element.attr({
          style: "stroke: var(--white-20); stroke-dasharray: 4px 4px",
        });
      }
      if (data.type === "grid" && data.axis.counterUnits.dir === "vertical") {
        data.element.attr({
          style: "stroke: transparent; ",
        });
      }
      if (data.type === "label") {
        data.element.attr({
          style: "color: var(--white-38); font-size: 13px;",
        });
      }
    });
    this.setState({ loading: false });
  }

  handlePaymentsSorting() {
    this.setState({
      paymentsSorting: this.state.paymentsSorting === "asc" ? "desc" : "asc",
    });
  }

  handlePayoutsSorting() {
    this.setState({
      payoutsSorting: this.state.payoutsSorting === "asc" ? "desc" : "asc",
    });
  }

  handleDateSelector(e, selector) {
    e.preventDefault();
    this.setState({ loading: true });
    submit("/actions/refresh", {
      method: "GET",
      body: {
        selector,
      },
    });
  }

  handleDataLoad(e) {
    e.preventDefault();
    this.setState({ loading: true });
    submit("/actions/refresh", {
      method: "GET",
      body: {
        loadData: true,
      },
    });
  }

  render() {
    const { locals } = this.context;
    const { feIncomes = {} } = locals;
    const { selector, loadData } = this.props;
    const { incomes = {}, history = {}, allNetGrowing = {} } = feIncomes;
    let { currency } = feIncomes;
    currency = currency || "usd";
    const { payments = [], payouts = [] } = history;
    const { mrr = {} } = incomes;
    const netGrowing = {
      actualMonth:
        allNetGrowing && allNetGrowing.length > 0
          ? allNetGrowing.slice(-1)[0].net
          : 0,
      lastMonth:
        allNetGrowing && allNetGrowing.length > 1
          ? allNetGrowing.slice(-2)[0].net
          : 0,
    };
    const currencySymbol = getSymbolFromCurrency(currency);
    const { tooltip, paymentsSorting, payoutsSorting, loading } = this.state;
    const displayLoader = !loadData || loading;
    const theme = locals.activeUser.webAppTheme || "dark";

    return (
      <main className="incomes">
        <header>
          <div className="selector-container">
            <EarningSelector
              title="Net Earnings"
              total={netGrowing.actualMonth}
              from={netGrowing.lastMonth}
              tooltipMessage="The amount of earnings generated during the calendar month net of the app fee."
              currency={currency}
              date={`${this.date.month} ${this.date.year}`}
            />
            <EarningSelector
              title="Monthly Recurring Revenue"
              total={mrr.actualMonth}
              from={mrr.lastMonth}
              tooltipMessage="Is all of your recurring gross revenue normalized into a monthly amount"
              currency={currency}
              date={`${this.date.month} ${this.date.year}`}
            />
          </div>
        </header>
        {displayLoader && (
          <CustomLoader handleLoad={this.handleDataLoad} loading={loading} />
        )}

        <section
          className={`incomes-container${displayLoader ? " v-hidden" : ""}`}
        >
          <header>
            <h3>
              Net Earnings ({currencySymbol}
              {currency.toUpperCase()})
            </h3>
            {!!loadData && !loading && (
              <RangeSelectors
                selectedValue={selector}
                onClick={this.handleDateSelector}
              />
            )}
          </header>
          <div
            className="graph-container"
            style={{
              position: "relative",
            }}
          >
            <div className="graph"></div>
            <div
              role="tooltip"
              className="graph-tooltip animate__animated animate__fadeInUp"
              style={
                tooltip.show
                  ? {
                      position: "absolute",
                      top: tooltip.y,
                      left: tooltip.x,
                    }
                  : {
                      display: "none",
                    }
              }
            >
              {tooltip.content}
            </div>
          </div>
        </section>
        <section
          className={`history-container${displayLoader ? " v-hidden" : ""}`}
        >
          <PaymentsTable
            payments={payments}
            sorting={paymentsSorting}
            handleSort={this.handlePaymentsSorting}
            theme={theme}
          />
          <PayoutsTable
            payouts={payouts}
            sorting={payoutsSorting}
            handleSort={this.handlePayoutsSorting}
          />
        </section>
      </main>
    );
  }
}

export default View;
